import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { toysApi } from "@/lib/api";
import ToyCard from "@/components/ToyCard";
import { ArrowRight, Sparkles, ScrollText, Hand } from "lucide-react";

const Home = () => {
  const { t } = useLanguage();
  const [toys, setToys] = useState([]);

  useEffect(() => {
    toysApi.list().then((all) => {
      // available first (newest first), then sold (newest first), take 3
      const sorted = [...all].sort((a, b) => {
        if (a.available !== b.available) return a.available ? -1 : 1;
        return (b.created_at || "").localeCompare(a.created_at || "");
      });
      setToys(sorted.slice(0, 3));
    }).catch(() => setToys([]));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/5603000/pexels-photo-5603000.jpeg"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24 md:py-36">
          <div className="max-w-2xl animate-fade-up">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-terracotta">
              <Sparkles size={14} strokeWidth={1.5} /> {t.home.heroEyebrow}
            </span>
            <h1 className="font-serif-display text-5xl md:text-6xl lg:text-7xl text-ink mt-6 leading-[1.05]">
              {t.home.heroTitle}
            </h1>
            <p className="font-body text-base md:text-lg text-muted-foreground mt-6 leading-relaxed max-w-xl">
              {t.home.heroSubtitle}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/catalogue"
                data-testid="hero-cta-catalog"
                className="group inline-flex items-center gap-3 bg-terracotta text-background px-7 py-3.5 rounded-full font-body text-sm tracking-wide hover:shadow-warm transition-all"
              >
                {t.home.heroCta}
                <ArrowRight size={16} strokeWidth={1.5} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/about"
                data-testid="hero-cta-about"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-ink/20 text-ink font-body text-sm hover:bg-cream transition-all"
              >
                {t.home.heroSecondary}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PROMISES */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20 md:py-28">
        <div className="grid md:grid-cols-3 gap-10">
          {[
            { Icon: Hand, title: t.home.promiseOne, desc: t.home.promiseOneDesc },
            { Icon: Sparkles, title: t.home.promiseTwo, desc: t.home.promiseTwoDesc },
            { Icon: ScrollText, title: t.home.promiseThree, desc: t.home.promiseThreeDesc },
          ].map(({ Icon, title, desc }, i) => (
            <div
              key={i}
              data-testid={`promise-${i}`}
              className="border border-border rounded-2xl p-8 bg-cream/30 hover:bg-cream/60 transition-colors animate-fade-up"
              style={{ animationDelay: `${i * 100}ms` }}
            >
              <Icon size={28} strokeWidth={1.3} className="text-terracotta" />
              <h3 className="font-serif-display text-2xl text-ink mt-5">{title}</h3>
              <p className="font-body text-sm text-muted-foreground mt-3 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED TOYS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-12 md:py-20">
        <div className="flex items-end justify-between gap-6 mb-12">
          <div>
            <p className="font-script text-camel text-2xl">à adopter</p>
            <h2 className="font-serif-display text-4xl md:text-5xl text-ink mt-1">{t.home.featuredTitle}</h2>
            <p className="font-body text-sm text-muted-foreground mt-3 max-w-md">{t.home.featuredSubtitle}</p>
          </div>
          <Link
            to="/catalogue"
            data-testid="featured-see-all"
            className="hidden md:inline-flex items-center gap-2 text-sm font-body text-terracotta hover:gap-3 transition-all"
          >
            {t.nav.catalog} <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
          {toys.map((toy, i) => (
            <ToyCard key={toy.id} toy={toy} index={i} />
          ))}
        </div>
      </section>

      {/* STORY */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20 md:py-28">
        <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="relative">
            <div className="absolute -top-4 -left-4 w-full h-full border border-camel rounded-2xl" />
            <img
              src="/story-image.png"
              alt=""
              className="relative w-full h-[480px] object-cover rounded-2xl"
            />
          </div>
          <div>
            <p className="font-script text-camel text-2xl">l'atelier</p>
            <h2 className="font-serif-display text-4xl md:text-5xl text-ink mt-1 leading-tight">
              {t.home.storyTitle}
            </h2>
            <p className="font-body text-base text-muted-foreground mt-6 leading-relaxed">
              {t.home.storyBody}
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 mt-8 text-terracotta font-body text-sm hover:gap-3 transition-all"
              data-testid="story-learn-more"
            >
              {t.common.learnMore} <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
